// frontend/src/components/parent/ParentCharts.tsx
// Saf SVG tabanlı, dengeli, kurumsal ve okunabilir grafik bileşenleri

import { useState } from 'react';
import { HeatmapDay, ActivityReport, TimeTrendItem, CategoryDistributionItem } from '../../types/parent.types';

const TR_DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

// ─────────────────────────────────────────────────────────────────
// 1. ZAMAN GRAFİĞİ — ÇALIŞMA SÜRESİ & AKTİVİTE TRENDİ (AREA / LINE)
// ─────────────────────────────────────────────────────────────────
interface TimeTrendAreaChartProps {
  timeTrend: TimeTrendItem[];
  title?: string;
  isLoading?: boolean;
}

export function TimeTrendAreaChart({ timeTrend, title = 'Zaman & Aktivite Trendi', isLoading = false }: TimeTrendAreaChartProps) {
  const [metric, setMetric] = useState<'studyMinutes' | 'totalEvents'>('studyMinutes');

  const data = timeTrend || [];
  const values = data.map(d => metric === 'studyMinutes' ? d.studyMinutes : d.totalEvents);
  const maxVal = Math.max(...values, metric === 'studyMinutes' ? 60 : 5);

  const W = 520;
  const H = 130;
  const PAD = { top: 16, right: 16, bottom: 24, left: 34 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  // Noktaların koordinatları
  const points = data.map((d, i) => {
    const val = metric === 'studyMinutes' ? d.studyMinutes : d.totalEvents;
    const x = data.length > 1
      ? PAD.left + (i / (data.length - 1)) * chartW
      : PAD.left + chartW / 2;
    const y = PAD.top + chartH - (val / maxVal) * chartH;
    return { x, y, val, label: d.label };
  });

  // SVG Area path & Line path
  const linePath = points.length > 1
    ? points.reduce((acc, p, i) => `${acc} ${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)},${p.y.toFixed(1)}`, '')
    : points.length === 1
    ? `M ${PAD.left},${points[0].y} L ${PAD.left + chartW},${points[0].y}`
    : '';

  const areaPath = points.length > 1
    ? `${linePath} L ${points[points.length - 1].x.toFixed(1)},${PAD.top + chartH} L ${points[0].x.toFixed(1)},${PAD.top + chartH} Z`
    : '';

  return (
    <div className="pcd-chart-card pcd-sharp-card">
      <div className="pcd-chart-header">
        <div>
          <div className="pcd-chart-title">{title}</div>
          <div className="pcd-chart-sub">
            {metric === 'studyMinutes' ? 'Tahmini çalışma süresi (dakika)' : 'Tamamlanan aktivite sayısı'}
          </div>
        </div>
        <div className="pcd-chart-actions">
          <button
            className={`pcd-toggle-btn ${metric === 'studyMinutes' ? 'active' : ''}`}
            onClick={() => setMetric('studyMinutes')}
          >
            ⏱️ Süre
          </button>
          <button
            className={`pcd-toggle-btn ${metric === 'totalEvents' ? 'active' : ''}`}
            onClick={() => setMetric('totalEvents')}
          >
            📊 Adet
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="pcd-chart-loading">Veriler yükleniyor...</div>
      ) : data.length === 0 ? (
        <div className="pcd-chart-empty">Bu dönem için aktivite kaydı bulunmuyor.</div>
      ) : (
        <div className="pcd-svg-wrap">
          <svg viewBox={`0 0 ${W} ${H}`} className="pcd-svg-canvas">
            <defs>
              <linearGradient id="pcdAreaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Y ekseni kılavuz çizgileri */}
            {[0, 0.5, 1].map((frac) => {
              const y = PAD.top + chartH * (1 - frac);
              return (
                <g key={frac}>
                  <line
                    x1={PAD.left}
                    y1={y}
                    x2={W - PAD.right}
                    y2={y}
                    stroke="#F1F5F9"
                    strokeWidth={1}
                    strokeDasharray={frac === 0 ? 'none' : '3 3'}
                  />
                  <text
                    x={PAD.left - 6}
                    y={y + 3.5}
                    fontSize={9.5}
                    fill="#94A3B8"
                    fontWeight={600}
                    textAnchor="end"
                    fontFamily="var(--f-mono)"
                  >
                    {Math.round(maxVal * frac)}
                    {metric === 'studyMinutes' && frac > 0 ? 'm' : ''}
                  </text>
                </g>
              );
            })}

            {/* Area & Line */}
            {areaPath && <path d={areaPath} fill="url(#pcdAreaGrad)" />}
            {linePath && (
              <path
                d={linePath}
                fill="none"
                stroke="#0EA5E9"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Veri Noktaları & Etiketler */}
            {points.map((p, i) => (
              <g key={i} className="pcd-chart-point">
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={3.8}
                  fill="#FFFFFF"
                  stroke="#0EA5E9"
                  strokeWidth={2.2}
                />
                {p.val > 0 && (
                  <text
                    x={p.x}
                    y={p.y - 6}
                    fontSize={10}
                    fontWeight={700}
                    fill="#0F172A"
                    textAnchor="middle"
                    fontFamily="var(--f-mono)"
                  >
                    {p.val}{metric === 'studyMinutes' ? 'd' : ''}
                  </text>
                )}
                {/* X eksen etiketi */}
                <text
                  x={p.x}
                  y={H - 4}
                  fontSize={10}
                  fill="#64748B"
                  fontWeight={600}
                  textAnchor="middle"
                >
                  {p.label}
                </text>
              </g>
            ))}
          </svg>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 2. KATEGORİ & BRANŞ DAĞILIMI DONUT/PASTA GRAFİĞİ
// ─────────────────────────────────────────────────────────────────
interface CategoryDonutChartProps {
  categories: CategoryDistributionItem[];
  title?: string;
  embedded?: boolean;
}

export function CategoryDonutChart({ categories, title = 'Alan Dağılımı', embedded = false }: CategoryDonutChartProps) {
  const items = categories.length > 0
    ? categories
    : [
        { category: 'Python', count: 1, averageProgress: 65, color: '#00AEEF' },
        { category: 'Matematik', count: 1, averageProgress: 80, color: '#F59E0B' },
        { category: 'Fen', count: 1, averageProgress: 50, color: '#10B981' },
      ];

  const totalCount = items.reduce((acc, it) => acc + it.count, 0) || 1;

  const R = 30;
  const CX = 40;
  const CY = 40;
  const circumference = 2 * Math.PI * R;

  let accumulatedOffset = 0;
  const slices = items.map((it) => {
    const fraction = it.count / totalCount;
    const dashLength = fraction * circumference;
    const offset = accumulatedOffset;
    accumulatedOffset += dashLength;
    return {
      ...it,
      dashLength,
      gapLength: circumference - dashLength,
      offset,
      percent: Math.round(fraction * 100)
    };
  });

  const content = (
    <div className="pcd-donut-layout">
      <div className="pcd-donut-svg-wrap">
        <svg viewBox="0 0 80 80" style={{ width: 80, height: 80, flexShrink: 0 }}>
          <circle cx={CX} cy={CY} r={R} fill="none" stroke="#F1F5F9" strokeWidth={8} />
          {slices.map((slice, i) => (
            <circle
              key={i}
              cx={CX}
              cy={CY}
              r={R}
              fill="none"
              stroke={slice.color}
              strokeWidth={8}
              strokeDasharray={`${slice.dashLength} ${slice.gapLength}`}
              strokeDashoffset={-slice.offset}
              transform={`rotate(-90 ${CX} ${CY})`}
              style={{ transition: 'stroke-dasharray 0.5s ease' }}
            />
          ))}
          <text x={CX} y={CY - 1} textAnchor="middle" fontSize={12} fontWeight={800} fill="#0F172A" fontFamily="var(--f-display)">
            {items.length}
          </text>
          <text x={CX} y={CY + 9} textAnchor="middle" fontSize={8} fill="#64748B" fontWeight={700} textTransform="uppercase">
            Alan
          </text>
        </svg>
      </div>

      {/* Detay Açıklama Listesi */}
      <div className="pcd-donut-legend">
        {slices.slice(0, 3).map((slice, i) => (
          <div key={i} className="pcd-legend-row">
            <span className="pcd-legend-dot" style={{ background: slice.color }} />
            <div className="pcd-legend-info">
              <span className="pcd-legend-name">{slice.category}</span>
            </div>
            <span className="pcd-legend-pct" style={{ color: slice.color }}>
              %{slice.percent}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  if (embedded) {
    return <div className="pcd-embedded-donut">{content}</div>;
  }

  return (
    <div className="pcd-chart-card pcd-sharp-card">
      <div className="pcd-chart-title">{title}</div>
      {content}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 3. ÇUBUK GRAFİĞİ — PERİYOT KATEGORİSEL ETKİLEŞİM
// ─────────────────────────────────────────────────────────────────
interface PeriodBarProps {
  report: ActivityReport | null;
}

export function PeriodBarChart({ report }: PeriodBarProps) {
  if (!report) return null;

  const bars = [
    { label: 'Ders', value: report.lessonsCompleted, color: '#0EA5E9', max: Math.max(15, report.lessonsCompleted) },
    { label: 'Quiz', value: report.quizzesSolved, color: '#EF4444', max: Math.max(15, report.quizzesSolved) },
    { label: 'Proje', value: report.projectsSubmitted, color: '#F59E0B', max: Math.max(8, report.projectsSubmitted) },
    { label: 'AI Chat', value: report.aiChats, color: '#8B5CF6', max: Math.max(25, report.aiChats) },
    { label: 'Aktif Gün', value: report.activeDays, color: '#10B981', max: 30 },
  ];

  const W = 460;
  const H = 130;
  const PAD = { top: 16, right: 14, bottom: 24, left: 14 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const slotW = chartW / bars.length;
  const barW = 16; // İdeal ve okunaklı 16px

  return (
    <div className="pcd-chart-card pcd-sharp-card">
      <div className="pcd-chart-title">{report.label} — Faaliyet Dağılımı</div>
      <div className="pcd-svg-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="pcd-svg-canvas">
          {/* İnce taban çizgisi */}
          <line
            x1={PAD.left}
            y1={PAD.top + chartH}
            x2={W - PAD.right}
            y2={PAD.top + chartH}
            stroke="#E2E8F0"
            strokeWidth={1}
          />

          {bars.map((bar, i) => {
            const pct = Math.min(1, bar.value / bar.max);
            const barH = bar.value > 0 ? Math.max(6, pct * (chartH - 12)) : 3;
            const x = PAD.left + i * slotW + (slotW - barW) / 2;
            const y = PAD.top + chartH - barH;
            return (
              <g key={bar.label}>
                {/* Zarif çubuk */}
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={barH}
                  rx={4}
                  fill={bar.value > 0 ? bar.color : '#E2E8F0'}
                />
                {/* Değer */}
                <text
                  x={x + barW / 2}
                  y={bar.value > 0 ? Math.max(PAD.top + 9, y - 4) : PAD.top + chartH - 5}
                  fontSize={10.5}
                  fontWeight={700}
                  fill={bar.value > 0 ? bar.color : '#94A3B8'}
                  textAnchor="middle"
                  fontFamily="var(--f-mono)"
                >
                  {bar.value}
                </text>
                {/* Etiket */}
                <text
                  x={x + barW / 2}
                  y={H - 4}
                  fontSize={10.5}
                  fill="#64748B"
                  fontWeight={600}
                  textAnchor="middle"
                >
                  {bar.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 4. HAFTALIK GÜN YOĞUNLUĞU GRAFİĞİ
// ─────────────────────────────────────────────────────────────────
interface DayPatternChartProps {
  days: HeatmapDay[];
}

export function DayPatternChart({ days }: DayPatternChartProps) {
  const totals = [0, 0, 0, 0, 0, 0, 0];
  const counts = [0, 0, 0, 0, 0, 0, 0];

  for (const day of days) {
    const d = new Date(day.date);
    const dow = (d.getDay() + 6) % 7; // 0=Pzt
    totals[dow] += (day.activityCount ?? day.intensity);
    counts[dow]++;
  }

  const avgs = totals.map((t, i) => (counts[i] > 0 ? t / counts[i] : 0));
  const maxAvg = Math.max(...avgs, 1);

  const W = 360;
  const H = 130;
  const PAD = { top: 16, right: 10, bottom: 24, left: 10 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const slotW = chartW / 7;
  const barW = 14; // İdeal ve okunaklı 14px

  return (
    <div className="pcd-chart-card pcd-sharp-card">
      <div className="pcd-chart-title">Haftanın Aktif Günleri</div>
      <div className="pcd-chart-sub">Ortalama aktivite yoğunluğu</div>
      <div className="pcd-svg-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="pcd-svg-canvas">
          {/* Taban çizgisi */}
          <line
            x1={PAD.left}
            y1={PAD.top + chartH}
            x2={W - PAD.right}
            y2={PAD.top + chartH}
            stroke="#E2E8F0"
            strokeWidth={1}
          />
          {avgs.map((avg, i) => {
            const barH = avg > 0 ? Math.max(6, (avg / maxAvg) * (chartH - 12)) : 3;
            const x = PAD.left + i * slotW + (slotW - barW) / 2;
            const y = PAD.top + chartH - barH;
            const isWeekend = i >= 5;
            const fill = avg === 0 ? '#E2E8F0' : isWeekend ? '#8B5CF6' : '#0EA5E9';

            return (
              <g key={i}>
                <rect x={x} y={y} width={barW} height={barH} rx={4} fill={fill} />
                <text
                  x={x + barW / 2}
                  y={H - 4}
                  fontSize={10.5}
                  fill={isWeekend ? '#8B5CF6' : '#64748B'}
                  textAnchor="middle"
                  fontWeight={700}
                >
                  {TR_DAYS[i]}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 5. QUİZ BAŞARI RADIAL GAUGE (Donut)
// ─────────────────────────────────────────────────────────────────
interface QuizScoreDonutProps {
  score: number;
  totalQuizzes: number;
  passedCount?: number;
  embedded?: boolean;
}

export function QuizScoreDonut({ score, totalQuizzes, passedCount, embedded = false }: QuizScoreDonutProps) {
  const R = 30;
  const CX = 40;
  const CY = 40;
  const circumference = 2 * Math.PI * R;
  const dash = (score / 100) * circumference;
  const gap = circumference - dash;

  const color = score >= 80 ? '#10B981' : score >= 60 ? '#0EA5E9' : score >= 40 ? '#F59E0B' : '#EF4444';

  const content = (
    <div className="pcd-donut-wrap">
      <svg viewBox="0 0 80 80" style={{ width: 80, height: 80, flexShrink: 0 }}>
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="#F1F5F9" strokeWidth={8} />
        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="none"
          stroke={color}
          strokeWidth={8}
          strokeDasharray={`${dash} ${gap}`}
          transform={`rotate(-90 ${CX} ${CY})`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
        <text x={CX} y={CY - 1} textAnchor="middle" fontSize={13} fontWeight={800} fill="#0F172A" fontFamily="var(--f-mono)">
          %{score}
        </text>
        <text x={CX} y={CY + 9} textAnchor="middle" fontSize={7.5} fill="#64748B" fontWeight={700}>
          ORTALAMA
        </text>
      </svg>
      <div className="pcd-donut-meta">
        <div className="pcd-stat-mini">
          <span className="pcd-val">{totalQuizzes}</span>
          <span className="pcd-lbl">Quiz Çözüldü</span>
        </div>
        {passedCount !== undefined && (
          <div className="pcd-stat-mini">
            <span className="pcd-val" style={{ color: '#10B981' }}>{passedCount}</span>
            <span className="pcd-lbl">Başarılı</span>
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) {
    return <div className="pcd-embedded-donut">{content}</div>;
  }

  return (
    <div className="pcd-chart-card pcd-sharp-card">
      <div className="pcd-chart-title">Quiz Başarı Oranı</div>
      {content}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 6. DİNAMİK AKTİVİTE ÇUBUK GRAFİĞİ (Geriye uyumluluk)
// ─────────────────────────────────────────────────────────────────
interface WeeklyBarChartProps {
  data: { label: string; value: number }[];
  title?: string;
  isLoading?: boolean;
}

export function WeeklyBarChart({ data, title = 'Aktivite Analizi', isLoading = false }: WeeklyBarChartProps) {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const W = 460;
  const H = 130;
  const PAD = { top: 16, right: 14, bottom: 24, left: 14 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const slotW = chartW / (data.length || 1);
  const barW = 16;

  return (
    <div className="pcd-chart-card pcd-sharp-card">
      <div className="pcd-chart-title">{title}</div>
      {isLoading ? (
        <div className="pcd-chart-loading">Yükleniyor...</div>
      ) : data.length === 0 ? (
        <div className="pcd-chart-empty">Veri bulunmuyor.</div>
      ) : (
        <div className="pcd-svg-wrap">
          <svg viewBox={`0 0 ${W} ${H}`} className="pcd-svg-canvas">
            <line
              x1={PAD.left}
              y1={PAD.top + chartH}
              x2={W - PAD.right}
              y2={PAD.top + chartH}
              stroke="#E2E8F0"
              strokeWidth={1}
            />
            {data.map((item, i) => {
              const barH = item.value > 0 ? Math.max(6, (item.value / maxVal) * (chartH - 12)) : 3;
              const x = PAD.left + i * slotW + (slotW - barW) / 2;
              const y = PAD.top + chartH - barH;
              return (
                <g key={i}>
                  <rect x={x} y={y} width={barW} height={barH} rx={4} fill={item.value > 0 ? '#0EA5E9' : '#E2E8F0'} />
                  {item.value > 0 && (
                    <text x={x + barW / 2} y={y - 4} fontSize={10.5} fontWeight={700} fill="#0F172A" textAnchor="middle">
                      {item.value}
                    </text>
                  )}
                  <text x={x + barW / 2} y={H - 4} fontSize={10.5} fill="#64748B" textAnchor="middle">
                    {item.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
}



